// Patient-facing payment status + Pay Now. Reads GET /my-payment-items
// (copay + up to 4 Proove panels, bundle-aware status) and drives Pay Now
// via POST /payments/mint-checkout-token -> POST /payments/create-checkout-
// session -> redirect, the same pattern Register.js's PanelCheckoutScreen
// already uses and has been security-reviewed. Modeled on MySummaries.js's
// scaffolding (incl. dark-mode support via useTheme, missing from this
// file's earlier single-copay-only version).
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { Box, Typography, CircularProgress, Chip, Button } from "@mui/material";
import { ReceiptLong, ArrowBack, OpenInNew } from "@mui/icons-material";
import axios from "axios";
import UserContext from "./UserContext";
import { useContext } from "react";
import ThemeToggle from "./ThemeToggle";
import NavUserMenu from "./NavUserMenu";
import { formatEST } from "../dateUtils";
import { extractErrorDetail } from "./validators";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BRAND = { navy: "#00357a", navyDark: "#001f4d", gold: "#c9a96e" };

// Copay vocabulary — unchanged from the original single-record view.
const COPAY_STATUS_LABEL = {
  completed: "Paid",
  pending: "Payment pending",
  failed: "Payment failed",
};
const COPAY_STATUS_COLOR = {
  completed: "success",
  pending: "warning",
  failed: "error",
};

// Panel vocabulary — deliberately separate from the copay maps above
// (GET /my-payment-items uses "paid"/"pending"/"unpaid" for panels, not
// "completed"/"pending"/"failed" — a real, distinct vocabulary per the
// backend's own docstring, flagged during Part 4's review as something
// that must not silently reuse the copay maps).
const PANEL_STATUS_LABEL = {
  paid: "Paid",
  pending: "Payment pending",
  unpaid: "Not yet paid",
};
const PANEL_STATUS_COLOR = {
  paid: "success",
  pending: "warning",
  unpaid: "default",
};

// 2026-10-16 (advisor finding, Part 9 close-out) — kept DISTINCT from
// PANEL_STATUS_LABEL/COLOR above: the subscription's "covered" status is
// "active" (a recurring, still-billing state), never "paid" (a one-time,
// closed transaction) — the same vocabulary-collision risk flagged and
// deliberately avoided in Part 4b's own MyPayments build.
const SUBSCRIPTION_STATUS_LABEL = {
  active: "Active",
  pending: "Checkout started",
  unpaid: "Not started",
  // Item 14 (2026-10-16) — distinct from "unpaid" (never attempted): a
  // subscription that WAS active and has since ended (via the Billing
  // Portal's own cancel flow, or any other Stripe-side cancellation).
  canceled: "Canceled",
};
const SUBSCRIPTION_STATUS_COLOR = {
  active: "success",
  pending: "warning",
  unpaid: "default",
  canceled: "default",
};

function formatCents(cents) {
  if (cents == null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

export default function MyPayments() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { user } = useContext(UserContext);
  const token = user?.token;
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payingKey, setPayingKey] = useState(null);
  const [payError, setPayError] = useState("");

  useEffect(() => {
    // Same role-gate fix as the prior version of this file — /my-payment-
    // items is patient-only (backend 404s any non-patient), but this route
    // is wrapped in a plain ProtectedRoute with no requiredRole.
    if (user?.role !== "patient") {
      setError("Payment details are only available for patient accounts.");
      setLoading(false);
      return;
    }
    axios.get(`${API_BASE}/my-payment-items`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setItems(res.data))
      .catch(() => setError("Unable to load your payment information right now."))
      .finally(() => setLoading(false));
  }, [token, user?.role]);

  const handlePay = async (itemKey) => {
    if (payingKey !== null) return; // guard re-entry, same pattern as Register.js's PanelCheckoutScreen
    setPayError("");
    setPayingKey(itemKey);
    try {
      const mintRes = await axios.post(
        `${API_BASE}/payments/mint-checkout-token`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const res = await axios.post(`${API_BASE}/payments/create-checkout-session`, {
        checkout_token: mintRes.data.checkout_token, item_key: itemKey,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      setPayingKey(null);
      setPayError(extractErrorDetail(err, "Could not start checkout — please try again."));
    }
  };

  // Item 14 (2026-10-16) — Stripe Billing Portal. Separate loading/error
  // state from handlePay's own payingKey/payError (a genuinely different
  // action — opening Stripe's own hosted portal, not this app's checkout
  // flow) so the two never fight over the same button's disabled/spinner
  // state. "Manage Subscription" opens the portal for cancel/payment-
  // method-update/receipt-history; "restart" a FULLY dead subscription is
  // NOT possible inside the portal itself (confirmed from Stripe's own
  // docs) and instead reuses handlePay above (the same "Start Subscription"
  // button already shown for an unpaid/pending subscription, further down
  // this file) — two deliberately different buttons/flows, never conflated.
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState("");

  const handleManageSubscription = async () => {
    if (portalLoading) return;
    setPortalError("");
    setPortalLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/payments/billing-portal-session`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.portal_url;
    } catch (err) {
      setPortalLoading(false);
      setPortalError(extractErrorDetail(err, "Could not open the billing portal right now — please try again."));
    }
  };

  const panelCardStyle = {
    borderRadius: 3, overflow: "hidden",
    border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.1)",
    bgcolor: isDark ? "#111827" : "#fff",
    boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,53,122,0.06)",
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: isDark ? "#080d1a" : "#f8f6f2" }}>
      <Box sx={{
        px: 3, py: 2, bgcolor: isDark ? "#111827" : "#fff",
        borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.08)",
        display: "flex", alignItems: "center", gap: 2,
      }}>
        <Box onClick={() => navigate("/home")}
          sx={{
            display: "flex", alignItems: "center", gap: 0.5,
            color: isDark ? "#94a3b8" : "#64748b", fontSize: 14.5, cursor: "pointer",
            borderRadius: "8px", px: 1.2, py: 0.6,
            transition: "all 0.2s",
            "&:hover": { color: isDark ? BRAND.gold : BRAND.navy, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,53,122,0.06)" },
          }}
        >
          <ArrowBack sx={{ fontSize: 15 }} />
          <span>Home</span>
        </Box>
        <Box sx={{ width: "1px", height: 20, bgcolor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,53,122,0.1)" }} />
        <Typography sx={{ fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, fontSize: 18, flex: 1 }}>My Payments</Typography>
        <ThemeToggle />
        <NavUserMenu />
      </Box>

      <Box sx={{ maxWidth: 760, mx: "auto", px: 3, py: 5 }}>
        <Typography sx={{ color: isDark ? "#94a3b8" : "#64748b", mb: 4 }}>
          Your Labarium account copay and lab test panel payment status, and receipts.
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: BRAND.navy }} /></Box>
        ) : error ? (
          <Typography sx={{ color: "#ef4444", textAlign: "center", py: 6 }}>{error}</Typography>
        ) : !items ? (
          <Box sx={{ textAlign: "center", py: 8, color: "#94a3b8" }}>
            <ReceiptLong sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
            <Typography>No payment record found yet.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {payError && (
              <Typography sx={{ color: "#ef4444", fontSize: 14, textAlign: "center" }}>{payError}</Typography>
            )}

            {/* Copay card — unchanged in substance from the single-record view. */}
            <Box sx={panelCardStyle}>
              <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${BRAND.navyDark} 0%, ${BRAND.navy} 100%)`, display: "flex", alignItems: "center", gap: 1.5 }}>
                <ReceiptLong sx={{ color: BRAND.gold, fontSize: 18 }} />
                <Typography sx={{ fontWeight: 700, color: "#fff", fontSize: 14.5 }}>Lab Test Copay and Activation Fee</Typography>
                <Chip
                  label={COPAY_STATUS_LABEL[items.copay.payment_status] || items.copay.payment_status}
                  color={COPAY_STATUS_COLOR[items.copay.payment_status] || "default"}
                  size="small"
                  sx={{ ml: "auto" }}
                />
              </Box>
              <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ color: isDark ? "#94a3b8" : "#64748b", fontSize: 14 }}>Amount</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: 14, color: isDark ? "#e2e8f0" : "inherit" }}>{formatCents(items.copay.payment_amount_cents)}</Typography>
                </Box>
                {items.copay.payment_confirmed_at && (
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography sx={{ color: isDark ? "#94a3b8" : "#64748b", fontSize: 14 }}>Confirmed</Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: 14, color: isDark ? "#e2e8f0" : "inherit" }}>{formatEST(items.copay.payment_confirmed_at)}</Typography>
                  </Box>
                )}
                {items.copay.payment_receipt_url && (
                  <Button
                    href={items.copay.payment_receipt_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    endIcon={<OpenInNew sx={{ fontSize: 16 }} />}
                    sx={{ mt: 1, alignSelf: "flex-start", textTransform: "none", color: isDark ? BRAND.gold : BRAND.navy, fontWeight: 600 }}
                  >
                    View Receipt
                  </Button>
                )}
              </Box>
            </Box>

            {/* Lab test panel cards — new in Part 4. */}
            {items.panels.length > 0 ? (
              <Typography sx={{ fontWeight: 700, color: isDark ? "#f1f5f9" : BRAND.navy, fontSize: 15, mt: 1 }}>
                Lab Test Panels
              </Typography>
            ) : (
              // 2026-10-15 fix (ui-ux-checker finding, copy sharpened per
              // advisor finding) — this used to render nothing at all when
              // panels is empty, leaving the intro paragraph's "...lab test
              // panel payment status" promise visibly unfulfilled with zero
              // explanation. The backend (/my-payment-items) only ever
              // returns an empty panels list when it couldn't build your
              // identity hashes (missing/undecryptable DOB or address) —
              // not "nothing to do" — so this tells the patient there's a
              // real account issue to resolve, not that everything's fine.
              <Typography sx={{ fontSize: 14, color: isDark ? "#64748b" : "#94a3b8", textAlign: "center", py: 2 }}>
                We couldn't match your account to any lab panels on file. If you're expecting to see a
                payment here, please contact{" "}
                <Box component="a" href="mailto:info@labarium.com" sx={{ color: isDark ? BRAND.gold : BRAND.navy, fontWeight: 600 }}>
                  info@labarium.com
                </Box>.
              </Typography>
            )}
            {items.panels.map((p) => (
              <Box key={p.item_key} sx={panelCardStyle}>
                <Box sx={{ px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: isDark ? "#e2e8f0" : "#1e293b" }}>{p.label}</Typography>
                    <Typography sx={{ fontSize: 13.5, color: isDark ? "#64748b" : "#94a3b8" }}>{formatCents(p.price_cents)}</Typography>
                  </Box>
                  <Chip
                    label={PANEL_STATUS_LABEL[p.status] || p.status}
                    color={PANEL_STATUS_COLOR[p.status] || "default"}
                    size="small"
                  />
                </Box>
                {p.status === "paid" && (p.payment_receipt_url || p.payment_confirmed_at) && (
                  <Box sx={{ px: 3, pb: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                    {p.payment_confirmed_at && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ color: isDark ? "#94a3b8" : "#64748b", fontSize: 13.5 }}>Confirmed</Typography>
                        <Typography sx={{ fontWeight: 600, fontSize: 13.5, color: isDark ? "#e2e8f0" : "inherit" }}>{formatEST(p.payment_confirmed_at)}</Typography>
                      </Box>
                    )}
                    {p.payment_receipt_url && (
                      <Button
                        href={p.payment_receipt_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        endIcon={<OpenInNew sx={{ fontSize: 16 }} />}
                        sx={{ alignSelf: "flex-start", textTransform: "none", color: isDark ? BRAND.gold : BRAND.navy, fontWeight: 600, fontSize: 13.5 }}
                      >
                        View Receipt
                      </Button>
                    )}
                  </Box>
                )}
                {(p.status === "unpaid" || p.status === "pending") && (
                  // 2026-10-15 fix (advisor finding, real dead-end bug) —
                  // "pending" used to show static text with NO way to pay
                  // from here, even though create_checkout_session's own
                  // backend logic happily reuses a pending row and lets
                  // the patient retry (it only 409s on "completed", never
                  // "pending") — a real gap, since PaymentCancelled's own
                  // copy explicitly promises "sign in to try again anytime
                  // from My Payments." Pay Now is now available for both
                  // statuses; the pending-specific note no longer claims a
                  // specific contact channel (a self-initiated, abandoned
                  // checkout never had any link "sent" at all — only an
                  // order-time link actually goes by email/SMS).
                  <Box sx={{ px: 3, pb: 2.5 }}>
                    {p.status === "pending" && (
                      <Typography sx={{ fontSize: 13, color: isDark ? "#64748b" : "#94a3b8", mb: 1 }}>
                        A payment for this panel was started but not completed — you can try again below.
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      disabled={payingKey !== null && payingKey !== p.item_key}
                      onClick={() => handlePay(p.item_key)}
                      aria-label={payingKey === p.item_key ? "Processing payment" : `Pay for ${p.label}`}
                      sx={{
                        background: BRAND.navy, borderRadius: "8px", textTransform: "none", fontWeight: 700,
                        px: 2.5, py: 1, fontSize: 14, "&:hover": { bgcolor: BRAND.navyDark },
                        "&.Mui-disabled": { background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.15)" },
                      }}>
                      {payingKey === p.item_key ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Pay Now"}
                    </Button>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* 2026-10-16 fix (advisor finding, Part 9 close-out, CONFIRMED
            real data-loss bug) — the subscription opt-in checkbox at
            registration lived entirely in Register.js's own in-memory
            React state, which a full-page redirect to Stripe for a panel
            payment (window.location.href above) wipes on return, with
            nothing persisted server-side to recover from. This is the
            recovery surface: null (hidden entirely) unless the patient
            actually opted in at registration, reusing the exact same
            generic handlePay/payingKey/panelCardStyle the panel cards
            above already use — same mechanism, separately labeled so it's
            never confused with a lab test panel. */}
        {/* 2026-10-16 (item 14 addition) — gate widened from "items?.
            subscription" alone to also include has_subscription_history:
            that flag is set purely from a real patient_payment_requests
            row (any status, never gated on subscription_optin/the price-
            env check items.subscription itself depends on), so a canceled
            patient whose subscription_optin happens to read false, or
            whose price env got unset after they already subscribed, must
            still be able to reach "Manage Subscription" — the whole point
            of this requirement (plan doc, item 14) is that the button is
            NOT conditioned on currently-active. */}
        {(items?.subscription || items?.has_subscription_history) && (
          <Box sx={{ mt: 3 }}>
            <Typography sx={{ fontWeight: 700, color: isDark ? "#f1f5f9" : BRAND.navy, fontSize: 15, mt: 1 }}>
              Monthly AI Subscription
            </Typography>
            <Box sx={panelCardStyle}>
              {items.subscription && (
                <Box sx={{ px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: isDark ? "#e2e8f0" : "#1e293b" }}>
                      {items.subscription.label}
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: isDark ? "#64748b" : "#94a3b8" }}>
                      {formatCents(items.subscription.price_cents)}/month
                    </Typography>
                  </Box>
                  <Chip
                    label={SUBSCRIPTION_STATUS_LABEL[items.subscription.status] || items.subscription.status}
                    color={SUBSCRIPTION_STATUS_COLOR[items.subscription.status] || "default"}
                    size="small"
                  />
                </Box>
              )}
              {items.subscription && (
                items.subscription.status === "unpaid"
                || items.subscription.status === "pending"
                || items.subscription.status === "canceled"
              ) && (
                <Box sx={{ px: 3, pb: 2.5 }}>
                  {items.subscription.status === "pending" && (
                    <Typography sx={{ fontSize: 13, color: isDark ? "#64748b" : "#94a3b8", mb: 1 }}>
                      A subscription checkout was started but not completed — you can try again below.
                    </Typography>
                  )}
                  {/* Item 14 (2026-10-16) — a canceled subscription cannot
                      be reactivated inside the Stripe Billing Portal itself
                      (confirmed from Stripe's own docs); "Restart
                      Subscription" here routes to this app's own existing
                      resubscribe flow (the same handlePay/create-checkout-
                      session path the "unpaid" case already uses), never
                      to anything inside the portal — two deliberately
                      different buttons/flows. */}
                  {items.subscription.status === "canceled" && (
                    <Typography sx={{ fontSize: 13, color: isDark ? "#64748b" : "#94a3b8", mb: 1 }}>
                      Your subscription was canceled. You can start a new one below at any time.
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    disabled={payingKey !== null && payingKey !== items.subscription.item_key}
                    onClick={() => handlePay(items.subscription.item_key)}
                    aria-label={payingKey === items.subscription.item_key ? "Processing subscription checkout" : "Start monthly AI subscription"}
                    sx={{
                      background: BRAND.navy, borderRadius: "8px", textTransform: "none", fontWeight: 700,
                      px: 2.5, py: 1, fontSize: 14, "&:hover": { bgcolor: BRAND.navyDark },
                      "&.Mui-disabled": { background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.15)" },
                    }}>
                    {payingKey === items.subscription.item_key
                      ? <CircularProgress size={18} sx={{ color: "#fff" }} />
                      : (items.subscription.status === "canceled" ? "Restart Subscription" : "Start Subscription")}
                  </Button>
                </Box>
              )}
              {/* Item 14 (2026-10-16) — "Manage Subscription" opens Stripe's
                  own hosted Billing Portal: cancel, update payment method,
                  view receipts/history. Shown for any patient who has a
                  real Stripe customer on file (has_billing_account) — NOT
                  the broader has_subscription_history, which is also true
                  for a merely "pending" row from an abandoned checkout that
                  never reached Stripe at all (advisor finding, pre-
                  integration: that case has no stripe_customer_id yet, so
                  the portal endpoint could only ever 409 for it). Not
                  gated on currently-active — a canceled patient with a real
                  billing account still needs to reach the portal to see
                  why/when their subscription ended. This is intentionally a
                  DIFFERENT button from "Start Subscription" above: a fully-
                  canceled subscription cannot be reactivated inside the
                  portal itself (Stripe's own documented limitation), so
                  that case always routes through "Start Subscription"
                  (handlePay), never this button. */}
              {items.has_billing_account && (
                <Box sx={{ px: 3, pb: 2.5, pt: items.subscription?.status === "active" ? 2 : 0 }}>
                  {portalError && (
                    <Typography sx={{ color: "#ef4444", fontSize: 13, mb: 1 }}>{portalError}</Typography>
                  )}
                  <Button
                    variant="outlined"
                    disabled={portalLoading}
                    onClick={handleManageSubscription}
                    aria-label={portalLoading ? "Opening billing portal" : "Manage subscription in Stripe billing portal"}
                    sx={{
                      borderColor: isDark ? "rgba(255,255,255,0.2)" : BRAND.navy,
                      color: isDark ? "#e2e8f0" : BRAND.navy,
                      borderRadius: "8px", textTransform: "none", fontWeight: 700,
                      px: 2.5, py: 1, fontSize: 14,
                      "&:hover": { borderColor: isDark ? "rgba(255,255,255,0.35)" : BRAND.navyDark, bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,53,122,0.04)" },
                    }}>
                    {portalLoading ? <CircularProgress size={18} sx={{ color: isDark ? "#e2e8f0" : BRAND.navy }} /> : "Manage Subscription"}
                  </Button>
                  <Typography sx={{ fontSize: 12.5, color: isDark ? "#64748b" : "#94a3b8", mt: 1 }}>
                    Cancel, update your payment method, or view past receipts — handled securely by Stripe.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
